import { v4 as uuidv4 } from 'uuid'
import type { ChunkLookupSpan, GraphQLLookupProject } from './query'
import { spanIsLicensed } from './licensed-display'
import { parseTimecodeToSeconds } from './timecode'

export const PROJECT_STORAGE_KEY = 'transcript-chunk-search-projects:v1'
export const PROJECT_SCHEMA_VERSION = 1

export type ProjectSearchContext = {
  prompt?: string
  language?: string
}

export type ProjectClip = {
  id: string
  contentRowId: string
  featureMediaId: string
  lemonadeContentId?: number
  startTimecode: string
  endTimecode: string
  originalStartTimecode: string
  originalEndTimecode: string
  transcriptText?: string | null
  matchedText?: string | null
  contentTitle?: string | null
  seriesTitle?: string | null
  contentPath?: string | null
  isLicensed?: boolean
  addedAt: string
  /** Search language used when the clip was added (EN, ES, DE, FR). */
  searchLanguage?: string
}

export type TranscriptChunkProject = {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  clips: ProjectClip[]
  lastSearchContext?: ProjectSearchContext
  rawExportStatus?: string | null
  rawExportReadyAt?: string | null
  rawExportExpiresAt?: string | null
  rawExportRequestedAt?: string | null
  /** Bumped when clip edits invalidate export; guards stale download merges. */
  exportClearGeneration?: number
}

export function clearRawExportFields (project: TranscriptChunkProject): TranscriptChunkProject {
  return {
    ...project,
    rawExportStatus: null,
    rawExportReadyAt: null,
    rawExportExpiresAt: null,
    rawExportRequestedAt: null,
    exportClearGeneration: (project.exportClearGeneration ?? 0) + 1,
  }
}

export type ProjectStoreV1 = {
  schemaVersion: 1
  activeProjectId: string | null
  projects: TranscriptChunkProject[]
}

export type ProjectExportFile = {
  schemaVersion: 1
  exportType: 'project'
  project: TranscriptChunkProject
}

export type StoreExportFile = {
  schemaVersion: 1
  exportType: 'store'
  activeProjectId: string | null
  projects: TranscriptChunkProject[]
}

export type TimecodeValidation = {
  valid: boolean
  startError?: string
  endError?: string
}

export type ParseImportResult =
  | { ok: true; kind: 'project'; project: TranscriptChunkProject }
  | { ok: true; kind: 'store'; store: ProjectStoreV1 }
  | { ok: false; error: string }

export function clipKey (
  clip: Pick<ProjectClip, 'contentRowId' | 'startTimecode' | 'endTimecode'>,
): string {
  return `${clip.contentRowId}-${clip.startTimecode}-${clip.endTimecode}`
}

export function spanToClipKey (
  span: Pick<ChunkLookupSpan, 'contentRowId' | 'startTimecode' | 'endTimecode'>,
): string {
  return clipKey({
    contentRowId: span.contentRowId,
    startTimecode: span.startTimecode,
    endTimecode: span.endTimecode,
  })
}

export function createEmptyProject (name = 'Untitled project'): TranscriptChunkProject {
  const now = new Date().toISOString()
  return {
    id: uuidv4(),
    name,
    createdAt: now,
    updatedAt: now,
    clips: [],
  }
}

export function createEmptyStore (): ProjectStoreV1 {
  const project = createEmptyProject()
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    activeProjectId: project.id,
    projects: [project],
  }
}

export function createEmptyProjectListStore (): ProjectStoreV1 {
  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    activeProjectId: null,
    projects: [],
  }
}

export function touchProject (project: TranscriptChunkProject): TranscriptChunkProject {
  return {
    ...project,
    updatedAt: new Date().toISOString(),
  }
}

export function projectClipFromSpan (span: ChunkLookupSpan): ProjectClip {
  const lemonadeContentId = span.content?.contentId
  return {
    id: uuidv4(),
    contentRowId: span.contentRowId,
    featureMediaId: span.featureMediaId ?? '',
    lemonadeContentId: lemonadeContentId != null ? Number(lemonadeContentId) : undefined,
    startTimecode: span.startTimecode,
    endTimecode: span.endTimecode,
    originalStartTimecode: span.startTimecode,
    originalEndTimecode: span.endTimecode,
    transcriptText: span.transcriptText,
    matchedText: span.matchedText,
    contentTitle: span.content?.title ?? null,
    seriesTitle: span.content?.contentSeries?.[0]?.title ?? null,
    contentPath: span.content?.path ?? null,
    isLicensed: spanIsLicensed(span),
    addedAt: new Date().toISOString(),
  }
}

export function validateClipTimecodes (
  startTimecode: string,
  endTimecode: string,
): TimecodeValidation {
  const startSec = parseTimecodeToSeconds(startTimecode)
  const endSec = parseTimecodeToSeconds(endTimecode)

  if (startSec == null) {
    return { valid: false, startError: 'Enter a valid start time.' }
  }
  if (endSec == null) {
    return { valid: false, endError: 'Enter a valid end time.' }
  }
  if (startSec >= endSec) {
    return {
      valid: false,
      endError: 'End time must be after start time.',
    }
  }
  return { valid: true }
}

export function isTimecodeOutsideOriginalSpan (
  startTimecode: string,
  endTimecode: string,
  originalStartTimecode: string,
  originalEndTimecode: string,
): boolean {
  const startSec = parseTimecodeToSeconds(startTimecode)
  const endSec = parseTimecodeToSeconds(endTimecode)
  const origStartSec = parseTimecodeToSeconds(originalStartTimecode)
  const origEndSec = parseTimecodeToSeconds(originalEndTimecode)
  if (startSec == null || endSec == null || origStartSec == null || origEndSec == null) {
    return false
  }
  return startSec < origStartSec || endSec > origEndSec
}

export function addClipFromSpan (
  project: TranscriptChunkProject,
  span: ChunkLookupSpan,
  searchContext?: ProjectSearchContext,
): { project: TranscriptChunkProject; added: boolean; duplicate: boolean; clipId?: string } {
  const key = spanToClipKey(span)
  const duplicate = project.clips.some((clip) => clipKey(clip) === key)
  if (duplicate) {
    return { project, added: false, duplicate: true }
  }

  const newClip = {
    ...projectClipFromSpan(span),
    searchLanguage: searchContext?.language,
  }
  const nextClips = [...project.clips, newClip]
  const next: TranscriptChunkProject = touchProject({
    ...project,
    clips: nextClips,
    lastSearchContext: searchContext ?? project.lastSearchContext,
  })
  return { project: next, added: true, duplicate: false, clipId: newClip.id }
}

export function removeClip (
  project: TranscriptChunkProject,
  clipId: string,
): TranscriptChunkProject {
  return touchProject({
    ...project,
    clips: project.clips.filter((clip) => clip.id !== clipId),
  })
}

export function updateClipTimecodes (
  project: TranscriptChunkProject,
  clipId: string,
  startTimecode: string,
  endTimecode: string,
): { project: TranscriptChunkProject; validation: TimecodeValidation } {
  const validation = validateClipTimecodes(startTimecode, endTimecode)
  if (!validation.valid) {
    return { project, validation }
  }

  const nextClips = project.clips.map((clip) => {
    if (clip.id !== clipId) {
      return clip
    }
    return {
      ...clip,
      startTimecode: startTimecode.trim(),
      endTimecode: endTimecode.trim(),
    }
  })

  return {
    project: touchProject({ ...project, clips: nextClips }),
    validation,
  }
}

export function reorderClips (
  project: TranscriptChunkProject,
  fromIndex: number,
  toIndex: number,
): TranscriptChunkProject {
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= project.clips.length ||
    toIndex >= project.clips.length ||
    fromIndex === toIndex
  ) {
    return project
  }

  const clips = [...project.clips]
  const [moved] = clips.splice(fromIndex, 1)
  clips.splice(toIndex, 0, moved)
  return touchProject({ ...project, clips })
}

export function clearProjectClips (project: TranscriptChunkProject): TranscriptChunkProject {
  return touchProject({ ...project, clips: [] })
}

export function renameProject (
  project: TranscriptChunkProject,
  name: string,
): TranscriptChunkProject {
  const trimmed = name.trim()
  if (!trimmed) {
    return project
  }
  return touchProject({ ...project, name: trimmed.slice(0, 128) })
}

export function updateStoreProject (
  store: ProjectStoreV1,
  projectId: string,
  project: TranscriptChunkProject,
): ProjectStoreV1 {
  return {
    ...store,
    projects: store.projects.map((p) => (p.id === projectId ? project : p)),
  }
}

export function addProjectToStore (store: ProjectStoreV1, name?: string): ProjectStoreV1 {
  const project = createEmptyProject(name)
  return {
    ...store,
    activeProjectId: project.id,
    projects: [...store.projects, project],
  }
}

export function deleteProjectFromStore (store: ProjectStoreV1, projectId: string): ProjectStoreV1 {
  const remaining = store.projects.filter((p) => p.id !== projectId)
  if (remaining.length === 0) {
    return createEmptyProjectListStore()
  }

  const activeProjectId =
    store.activeProjectId === projectId ? remaining[0].id : store.activeProjectId

  return {
    ...store,
    activeProjectId,
    projects: remaining,
  }
}

export function setActiveProject (store: ProjectStoreV1, projectId: string): ProjectStoreV1 {
  if (!store.projects.some((p) => p.id === projectId)) {
    return store
  }
  return { ...store, activeProjectId: projectId }
}

export function getActiveProject (store: ProjectStoreV1): TranscriptChunkProject | null {
  if (!store.activeProjectId) {
    return store.projects[0] ?? null
  }
  return store.projects.find((p) => p.id === store.activeProjectId) ?? store.projects[0] ?? null
}

export function isSpanInProject (
  project: TranscriptChunkProject | null,
  span: Pick<ChunkLookupSpan, 'contentRowId' | 'startTimecode' | 'endTimecode'>,
): boolean {
  if (!project) {
    return false
  }
  const key = spanToClipKey(span)
  return project.clips.some((clip) => clipKey(clip) === key)
}

function isRecord (value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function parseClip (raw: unknown): ProjectClip | null {
  if (!isRecord(raw)) {
    return null
  }
  const {
    id,
    contentRowId,
    featureMediaId,
    startTimecode,
    endTimecode,
    originalStartTimecode,
    originalEndTimecode,
  } = raw
  if (
    typeof id !== 'string' ||
    typeof contentRowId !== 'string' ||
    typeof featureMediaId !== 'string' ||
    typeof startTimecode !== 'string' ||
    typeof endTimecode !== 'string' ||
    typeof originalStartTimecode !== 'string' ||
    typeof originalEndTimecode !== 'string'
  ) {
    return null
  }

  const validation = validateClipTimecodes(startTimecode, endTimecode)
  if (!validation.valid) {
    return null
  }

  return {
    id,
    contentRowId,
    featureMediaId,
    lemonadeContentId:
      typeof raw.lemonadeContentId === 'number' ? raw.lemonadeContentId : undefined,
    startTimecode,
    endTimecode,
    originalStartTimecode,
    originalEndTimecode,
    transcriptText: typeof raw.transcriptText === 'string' ? raw.transcriptText : null,
    matchedText: typeof raw.matchedText === 'string' ? raw.matchedText : null,
    contentTitle: typeof raw.contentTitle === 'string' ? raw.contentTitle : null,
    seriesTitle: typeof raw.seriesTitle === 'string' ? raw.seriesTitle : null,
    contentPath: typeof raw.contentPath === 'string' ? raw.contentPath : null,
    isLicensed: raw.isLicensed === true,
    addedAt: typeof raw.addedAt === 'string' ? raw.addedAt : new Date().toISOString(),
    searchLanguage:
      typeof raw.searchLanguage === 'string' && raw.searchLanguage ? raw.searchLanguage : undefined,
  }
}

function parseProject (raw: unknown): TranscriptChunkProject | null {
  if (!isRecord(raw)) {
    return null
  }
  const { id, name, createdAt, updatedAt, clips } = raw
  if (
    typeof id !== 'string' ||
    typeof name !== 'string' ||
    typeof createdAt !== 'string' ||
    typeof updatedAt !== 'string' ||
    !Array.isArray(clips)
  ) {
    return null
  }

  const parsedClips = clips.map(parseClip).filter((clip): clip is ProjectClip => clip != null)
  if (parsedClips.length !== clips.length) {
    return null
  }

  const lastSearchContext = isRecord(raw.lastSearchContext)
    ? {
        prompt:
          typeof raw.lastSearchContext.prompt === 'string'
            ? raw.lastSearchContext.prompt
            : undefined,
        language:
          typeof raw.lastSearchContext.language === 'string'
            ? raw.lastSearchContext.language
            : undefined,
      }
    : undefined

  return {
    id,
    name: name.trim().slice(0, 128) || 'Imported project',
    createdAt,
    updatedAt,
    clips: parsedClips,
    lastSearchContext,
  }
}

export function parseStore (raw: unknown): ProjectStoreV1 | null {
  if (!isRecord(raw) || raw.schemaVersion !== PROJECT_SCHEMA_VERSION) {
    return null
  }
  if (!Array.isArray(raw.projects)) {
    return null
  }

  const projects = raw.projects
    .map(parseProject)
    .filter((p): p is TranscriptChunkProject => p != null)
  if (projects.length === 0) {
    return null
  }

  const activeProjectId =
    typeof raw.activeProjectId === 'string' && projects.some((p) => p.id === raw.activeProjectId)
      ? raw.activeProjectId
      : projects[0].id

  return {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    activeProjectId,
    projects,
  }
}

export function parseImportedJson (text: string): ParseImportResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { ok: false, error: 'File is not valid JSON.' }
  }

  if (!isRecord(parsed) || parsed.schemaVersion !== PROJECT_SCHEMA_VERSION) {
    return { ok: false, error: 'Unsupported project file version.' }
  }

  if (parsed.exportType === 'project') {
    const project = parseProject(parsed.project)
    if (!project) {
      return { ok: false, error: 'Project file is missing required fields.' }
    }
    return { ok: true, kind: 'project', project }
  }

  if (parsed.exportType === 'store') {
    const store = parseStore(parsed)
    if (!store) {
      return { ok: false, error: 'Store file is missing required fields.' }
    }
    return { ok: true, kind: 'store', store }
  }

  const legacyProject = parseProject(parsed)
  if (legacyProject) {
    return { ok: true, kind: 'project', project: legacyProject }
  }

  const legacyStore = parseStore(parsed)
  if (legacyStore) {
    return { ok: true, kind: 'store', store: legacyStore }
  }

  return { ok: false, error: 'Unrecognized project file shape.' }
}

export function serializeProjectExport (project: TranscriptChunkProject): string {
  const payload: ProjectExportFile = {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    exportType: 'project',
    project,
  }
  return JSON.stringify(payload, null, 2)
}

export function serializeStoreExport (store: ProjectStoreV1): string {
  const payload: StoreExportFile = {
    schemaVersion: PROJECT_SCHEMA_VERSION,
    exportType: 'store',
    activeProjectId: store.activeProjectId,
    projects: store.projects,
  }
  return JSON.stringify(payload, null, 2)
}

export function loadStoreFromLocalStorage (): ProjectStoreV1 {
  if (typeof window === 'undefined' || !window.localStorage) {
    return createEmptyStore()
  }

  try {
    const raw = window.localStorage.getItem(PROJECT_STORAGE_KEY)
    if (!raw) {
      return createEmptyStore()
    }
    const parsed = parseStore(JSON.parse(raw))
    return parsed ?? createEmptyStore()
  } catch {
    return createEmptyStore()
  }
}

export function saveStoreToLocalStorage (store: ProjectStoreV1): void {
  if (typeof window === 'undefined' || !window.localStorage) {
    return
  }
  window.localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(store))
}

export function slugifyProjectName (name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'project'
}

export function replaceActiveProjectInStore (
  store: ProjectStoreV1,
  project: TranscriptChunkProject,
): ProjectStoreV1 {
  const activeId = store.activeProjectId
  if (!activeId) {
    return importProjectAsNew(store, project)
  }

  const replaced: TranscriptChunkProject = {
    ...project,
    id: activeId,
    updatedAt: new Date().toISOString(),
  }
  return updateStoreProject(store, activeId, replaced)
}

export function importProjectAsNew (
  store: ProjectStoreV1,
  project: TranscriptChunkProject,
): ProjectStoreV1 {
  const imported: TranscriptChunkProject = {
    ...project,
    id: uuidv4(),
    clips: project.clips.map((clip) => ({ ...clip, id: uuidv4() })),
    updatedAt: new Date().toISOString(),
  }
  return {
    ...store,
    activeProjectId: imported.id,
    projects: [...store.projects, imported],
  }
}

export function mapGraphQLProjectToLocal (project: GraphQLLookupProject): TranscriptChunkProject {
  return {
    id: project.id,
    name: project.name,
    createdAt: project.createdAt ?? new Date().toISOString(),
    updatedAt: project.updatedAt ?? new Date().toISOString(),
    clips: (project.clips ?? []).map((clip) => ({
      id: clip.id,
      contentRowId: clip.contentRowId,
      featureMediaId: clip.featureMediaId,
      lemonadeContentId: clip.lemonadeContentId ?? undefined,
      startTimecode: clip.startTimecode,
      endTimecode: clip.endTimecode,
      originalStartTimecode: clip.originalStartTimecode,
      originalEndTimecode: clip.originalEndTimecode,
      transcriptText: clip.transcriptText,
      matchedText: clip.matchedText,
      contentTitle: clip.contentTitle,
      seriesTitle: clip.seriesTitle,
      contentPath: clip.contentPath,
      isLicensed: clip.isLicensed ?? undefined,
      addedAt: clip.addedAt,
      searchLanguage: clip.searchLanguage ?? undefined,
    })),
    lastSearchContext: project.lastSearchContext
      ? {
          prompt: project.lastSearchContext.prompt ?? undefined,
          language: project.lastSearchContext.language ?? undefined,
        }
      : undefined,
    rawExportStatus: project.rawExportStatus ?? null,
    rawExportReadyAt: project.rawExportReadyAt ?? null,
    rawExportExpiresAt: project.rawExportExpiresAt ?? null,
    rawExportRequestedAt: project.rawExportRequestedAt ?? null,
  }
}

export function mapLocalProjectToGraphQLInput (project: TranscriptChunkProject) {
  return {
    name: project.name,
    clips: project.clips.map((clip) => ({
      id: clip.id,
      contentRowId: clip.contentRowId,
      featureMediaId: clip.featureMediaId,
      lemonadeContentId: clip.lemonadeContentId,
      startTimecode: clip.startTimecode,
      endTimecode: clip.endTimecode,
      originalStartTimecode: clip.originalStartTimecode,
      originalEndTimecode: clip.originalEndTimecode,
      transcriptText: clip.transcriptText,
      matchedText: clip.matchedText,
      contentTitle: clip.contentTitle,
      seriesTitle: clip.seriesTitle,
      contentPath: clip.contentPath,
      isLicensed: clip.isLicensed,
      addedAt: clip.addedAt,
      searchLanguage: clip.searchLanguage,
    })),
    lastSearchContext: project.lastSearchContext,
  }
}
